import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Key, Fingerprint, MonitorSmartphone } from 'lucide-react';
import { ActiveSessions } from './ActiveSessions';

const API = process.env.REACT_APP_BACKEND_URL;

export default function UserSettings() {
  const { user } = useAuth();
  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [changing, setChanging] = useState(false);

  const changePassword = async () => {
    if (!oldPass || !newPass) { toast.error('Fill in all fields'); return; }
    if (newPass !== confirmPass) { toast.error('Passwords do not match'); return; }
    if (newPass.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await axios.post(`${API}/api/auth/change-password`, { current_password: oldPass, new_password: newPass });
      toast.success('Password changed');
      setOldPass(''); setNewPass(''); setConfirmPass(''); setChanging(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    }
  };

  useEffect(() => { if (window.location.hash === '#sessions') setTimeout(() => document.getElementById('sessions')?.scrollIntoView({ behavior: 'smooth' }), 150); }, []);

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Settings</h1>

      {/* Change Password */}
      <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Key className="w-5 h-5 text-slate-500" /> Change Password
        </h3>
        {changing ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-600 font-medium">Current Password</label>
              <input type="password" value={oldPass} onChange={e => setOldPass(e.target.value)} placeholder="Enter current password"
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">New Password</label>
              <input type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Enter new password"
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Confirm New Password</label>
              <input type="password" value={confirmPass} onChange={e => setConfirmPass(e.target.value)} placeholder="Confirm new password"
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={changePassword} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium">
                Save Password
              </button>
              <button onClick={() => { setChanging(false); setOldPass(''); setNewPass(''); setConfirmPass(''); }}
                className="px-5 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg font-medium">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setChanging(true)}
            className="px-5 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg font-medium">
            Change Password
          </button>
        )}
      </div>

      {/* Where you're signed in */}
      <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-6" id="sessions" data-testid="settings-sessions">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <MonitorSmartphone className="w-5 h-5 text-slate-500" /> Where you're signed in
        </h3>
        <ActiveSessions />
      </div>

      {/* Biometric Login */}
      <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Fingerprint className="w-5 h-5 text-slate-500" /> Biometric Login
        </h3>
        <BiometricSetup />
      </div>
    </div>
  );
}

function BiometricSetup() {
  const [registering, setRegistering] = useState(false);
  const [hasCredential, setHasCredential] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const supported = typeof window !== 'undefined' && window.PublicKeyCredential;

  const register = async () => {
    if (!supported) { toast.error('Biometric not supported on this device'); return; }
    setRegistering(true);
    try {
      const token = localStorage.getItem('token');
      const optR = await axios.post(`${API_URL}/api/auth/webauthn/register-options`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const options = optR.data;

      options.challenge = Uint8Array.from(atob(options.challenge), c => c.charCodeAt(0));
      options.user.id = Uint8Array.from(atob(options.user.id), c => c.charCodeAt(0));
      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map(c => ({ ...c, id: Uint8Array.from(atob(c.id), ch => ch.charCodeAt(0)) }));
      }

      const credential = await navigator.credentials.create({ publicKey: options });

      const attestation = {
        id: credential.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
        response: {
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON))),
          attestationObject: btoa(String.fromCharCode(...new Uint8Array(credential.response.attestationObject))),
        },
        type: credential.type,
      };

      await axios.post(`${API_URL}/api/auth/webauthn/register-complete`, attestation, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Biometric login enabled!');
      setHasCredential(true);
    } catch (e) {
      toast.error('Failed to register biometric');
    }
    setRegistering(false);
  };

  if (!supported) {
    return <p className="text-sm text-slate-500">Biometric login is not supported on this device/browser.</p>;
  }

  return (
    <div>
      <p className="text-sm text-slate-600 mb-3">Use your fingerprint or Face ID to log in without typing your password.</p>
      <button onClick={register} disabled={registering}
        className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white rounded-lg font-medium">
        {registering ? 'Waiting for biometric...' : hasCredential ? 'Re-register Biometric' : 'Enable Biometric Login'}
      </button>
    </div>
  );
}
